import { info } from "console";
import { iSizeConfigurations } from "../models/configurations";
import { iDistinctUnit, iDistinctUnitSize, iSizeGroup } from "../models/sizeGroup";
import { iStorageSizeAdvanced } from "../models/storageSizeAdvanced";
import { TranslateWithReplace } from "./clientUtilityHelper";

export {
    generateUnitCardsFromUnits as GenerateUnitCardsFromUnits,
    generateCardsFromUnitSize as GenerateCardsFromUnitSize,
    generateCardsFromSizeGroup as GenerateCardsFromSizeGroup,
    populateGroupSizeRange as PopulateGroupSizeRange,
    generateAllUnitCardsFromGroup as GenerateAllUnitCardsFromGroup
};

function generateUnitCardsFromUnits(units: iDistinctUnit[], sizeShortName: string, sizeConfigurations: iSizeConfigurations, orderStep1Url: string, translations: any): iStorageSizeAdvanced[] {
    let storageSizes: iStorageSizeAdvanced[] = [];

    units.forEach(unit => {
        if (unit.info?.hasAvailable !== undefined
            && unit.info?.isLastOne !== undefined
            && unit.info?.price !== undefined
            && unit.info?.physicalSize !== undefined
        ) {
            const cardName = unit.info?.isHeated
                ? "Tempereret"
                : unit.info?.isCold
                    ? "Conteiner"
                    : "";
            const price = unit.info?.ordPrice && unit.info?.ordPrice > 0 ? unit.info?.ordPrice : unit.info?.price;
            const specialPrice = unit.info?.ordPrice && unit.info?.ordPrice > 0 ? unit.info?.price : unit.info?.ordPrice;
            const volumeString = unit.info.volume ? unit.info?.volume.toFixed(1).endsWith(".0") ? parseInt(unit.info?.volume.toFixed(1)).toString() : unit.info?.volume.toFixed(1) : "";
            let infoDescription = TranslateWithReplace(translations.blocks.storageSizesAdvanced.infoDescriptionUnit, {
                size: unit.info?.physicalSize?.toString() ?? unit.info?.sizeInM2 ?? "",
                movingBoxes: unit.info?.sizeGroupShortName ? sizeConfigurations?.movingBoxes[unit.info?.sizeGroupShortName] ?? "" : ""
            });

            const storageItem: iStorageSizeAdvanced = {
                title: cardName,
                hasAvailable: unit.info?.hasAvailable,
                isLastOne: unit.info?.isLastOne,
                hasFewLeft: unit.info?.hasFewLeft ?? false,
                name: unit.info?.sizeGroupName ?? cardName,
                shortName: unit.info?.sizeGroupShortName ?? cardName,
                price: price || 0,
                size: unit.info?.physicalSize?.toString(),
                firstMonthDiscount: unit.info?.hasDiscount ?? false,
                specialPrice: specialPrice || 0,
                description: unit.info?.sizeInM2,
                link: `${orderStep1Url}?siteId=${unit.siteId}&groupName=${unit.info?.sizeGroupName}&unitId=${unit.unitId}`,
                movingBoxesCount: sizeConfigurations?.movingBoxes[sizeShortName] ?? "",
                id: unit.unitId, //TODO: Get this from correct source: id: size.SizeId,
                identifier: `Unit|${unit.unitId}|${unit.siteId}`,
                isHeated: unit.info?.isHeated,
                isCold: unit.info?.isCold,
                hasDirectAccess: unit.info?.hasDirectAccess,
                hasStairAccess: unit.info?.hasStairAccess,
                isGroundFloor: unit.info?.isGroundFloor,
                hasSlantedRoof: unit.info?.hasSlantedRoof,
                volume: volumeString,
                infoTitle: unit.unitId,
                infoDescription: infoDescription,
                infoVolumeString: `${translations.blocks.storageSizesAdvanced.ca} ${volumeString}`,
                facility: unit.facilityName,
                unitId: unit.unitId
            };

            let infoAccessList: string[] = [];

            if (storageItem.isGroundFloor) {
                infoAccessList.push(translations.general.groundFloor);
            } else if (storageItem.hasStairAccess) {
                infoAccessList.push(translations.general.stairAccess);
            } else {
                infoAccessList.push(translations.general.elevatorPlane);
            }

            if (!storageItem.hasDirectAccess) {
                infoAccessList.push(translations.general.directAccess);
            }

            if (storageItem.hasSlantedRoof) {
                infoAccessList.push(translations.general.slantedRoof);
            }

            if (infoAccessList.length > 0) {
                storageItem.infoAccessString = infoAccessList.join(", ");
            }

            storageSizes.push(storageItem)
        }
    });

    return storageSizes;
}

function generateCardsFromUnitSize(unitSizes: iDistinctUnitSize[], sizeShortName: string, orderStep1Url: string, chooseFacilityUrl: string, sizeConfigurations: iSizeConfigurations, translations: any, useHrefLink: boolean = false): iStorageSizeAdvanced[] {
    let storageSizes: iStorageSizeAdvanced[] = [];

    unitSizes.forEach(unitSize => {
        if (unitSize.info?.hasAvailable !== undefined
            && unitSize.info?.isLastOne !== undefined
            && unitSize.info?.price !== undefined
            && unitSize.info?.physicalSize !== undefined
        ) {
            const price = unitSize.info?.ordPrice && unitSize.info?.ordPrice > 0 ? unitSize.info?.ordPrice : unitSize.info?.price;
            const specialPrice = unitSize.info?.ordPrice && unitSize.info?.ordPrice > 0 ? unitSize.info?.price : unitSize.info?.ordPrice;
            // const volumeString = unitSize.info?.volume?.toFixed(1).endsWith(".0") ? parseInt(unitSize.info?.volume.toFixed(1)).toString() : unitSize.info?.volume?.toFixed(1);
            const volumeString = unitSize.info?.volume
                ? unitSize.info.volume.toFixed(1).endsWith(".0")
                    ? parseInt(unitSize.info.volume.toFixed(1)).toString()
                    : unitSize.info.volume.toFixed(1)
                : "";
            let cardLink = useHrefLink ? `${chooseFacilityUrl}?sizeCodeId=${unitSize.info.sizeCodeId}&groupName=${unitSize.info.sizeGroupName}` : "";
            let infoTitle = translations.blocks.storageSizesAdvanced.infoTitle;
            let infoDescription = TranslateWithReplace(translations.blocks.storageSizesAdvanced.infoDescription, { size: unitSize.info?.physicalSize?.toString(), movingBoxes: unitSize.info?.sizeGroupShortName ? sizeConfigurations?.movingBoxes[unitSize.info?.sizeGroupShortName] ?? "" : "" });

            if (unitSize.info?.isLastOne && unitSize.units?.length) {
                const lastUnit = unitSize.units[unitSize.units.length - 1];
                cardLink = `${orderStep1Url}?siteId=${lastUnit.siteId}&groupName=${lastUnit.info?.sizeGroupName}&unitId=${lastUnit.unitId}`;
                infoTitle = lastUnit.unitId;
                infoDescription = TranslateWithReplace(translations.blocks.storageSizesAdvanced.infoDescriptionUnit, {
                    size: lastUnit.info?.physicalSize?.toString() ?? lastUnit.info?.sizeInM2 ?? "",
                    movingBoxes: lastUnit.info?.sizeGroupShortName ? sizeConfigurations?.movingBoxes[lastUnit.info?.sizeGroupShortName] ?? "" : ""
                });
            }

            let listVolumeString = "";

            if (unitSize.info?.isLastOne) {
                listVolumeString = `${translations.blocks.storageSizesAdvanced.ca} ${volumeString}`;
            } else {
                listVolumeString = `${translations.general.from} ${translations.blocks.storageSizesAdvanced.ca} ${volumeString}`;
            }

            const storageItem: iStorageSizeAdvanced = {
                title: unitSize.title,
                hasAvailable: unitSize.info?.hasAvailable,
                isLastOne: unitSize.info?.isLastOne,
                hasFewLeft: unitSize.info?.hasFewLeft ?? false,
                name: unitSize.info?.sizeGroupName ?? unitSize.title,
                shortName: unitSize.info?.sizeGroupShortName ?? unitSize.title,
                price: price || 0,
                size: unitSize.info?.physicalSize?.toString(),
                firstMonthDiscount: unitSize.info?.hasDiscount ?? false,
                specialPrice: specialPrice || 0,
                description: unitSize.info?.sizeInM2, //TODO: Get this from correct source,
                link: cardLink,
                movingBoxesCount: sizeConfigurations?.movingBoxes?.[sizeShortName] ?? "",
                // movingBoxesCount: sizeConfigurations?.movingBoxes[sizeShortName] ?? "",
                id: unitSize.info?.sizeGroupName ?? "", //TODO: Get this from correct source: id: size.SizeId,
                identifier: `${sizeShortName}|${unitSize.info?.physicalSize}`,
                isHeated: unitSize.info?.isHeated,
                isCold: unitSize.info?.isCold,
                hasDirectAccess: unitSize.info?.hasDirectAccess,
                hasStairAccess: unitSize.info?.hasStairAccess,
                isGroundFloor: unitSize.info?.isGroundFloor,
                hasSlantedRoof: unitSize.info?.hasSlantedRoof,
                volume: unitSize.info?.isLastOne
                    ? volumeString
                    : `${translations.blocks.storageSizesAdvanced.ca} ${volumeString}`,
                infoTitle: infoTitle,
                infoDescription: infoDescription,
                infoVolumeString: listVolumeString
            };

            let infoAccessList: string[] = [];

            if (storageItem.isGroundFloor) {
                infoAccessList.push(translations.general.groundFloor);
            } else if (storageItem.hasStairAccess) {
                infoAccessList.push(translations.general.stairAccess);
            } else {
                infoAccessList.push(translations.general.elevatorPlane);
            }

            if (!storageItem.hasDirectAccess) {
                infoAccessList.push(translations.general.directAccess);
            }

            if (storageItem.hasSlantedRoof) {
                infoAccessList.push(translations.general.slantedRoof);
            }

            if (infoAccessList.length > 0) {
                storageItem.infoAccessString = infoAccessList.join(", ");
            }

            storageSizes.push(storageItem)
        }
    });

    return storageSizes;
}

function generateCardsFromSizeGroup(groups: iSizeGroup[], orderStep1Url: string, chooseFacilityUrl: string, sizeConfigurations: iSizeConfigurations, translations: any, useHrefLink: boolean = false): iStorageSizeAdvanced[] {
    let storageSizes: iStorageSizeAdvanced[] = [];

    groups.forEach(group => {
        if (group.info?.hasAvailable !== undefined
            && group.info?.isLastOne !== undefined
            && group.info?.price !== undefined
            && group.info?.physicalSize !== undefined
        ) {
            const price = group.info?.ordPrice && group.info?.ordPrice > 0 ? group.info?.ordPrice : group.info?.price;
            const specialPrice = group.info?.ordPrice && group.info?.ordPrice > 0 ? group.info?.price : group.info?.ordPrice;
            const volumeString = group.info?.volume ? group.info?.volume?.toFixed(1).endsWith(".0") ? parseInt(group.info?.volume.toFixed(1)).toString() : group.info?.volume?.toFixed(1) : "";
            let cardLink = useHrefLink ? `${chooseFacilityUrl}?sizeCodeId=${group.sizeCodeId}&groupName=${group.name}` : "";
            let infoTitle = translations.blocks.storageSizesAdvanced.infoTitle;
            let infoDescription = TranslateWithReplace(translations.blocks.storageSizesAdvanced.infoDescription, { size: group.info?.physicalSize?.toString(), movingBoxes: group.info?.sizeGroupShortName ? sizeConfigurations?.movingBoxes[group.info?.sizeGroupShortName] ?? "" : "" });

            if (group.info?.isLastOne) {
                const unitSizeWithLastUnit = group.unitSizes.find(g => g.info?.isLastOne);

                if (unitSizeWithLastUnit) {
                    const lastUnit = unitSizeWithLastUnit.units[unitSizeWithLastUnit.units.length - 1];
                    cardLink = `${orderStep1Url}?siteId=${lastUnit.siteId}&groupName=${lastUnit.info?.sizeGroupName}&unitId=${lastUnit.unitId}`;
                    infoTitle = lastUnit.unitId;
                    infoDescription = TranslateWithReplace(translations.blocks.storageSizesAdvanced.infoDescriptionUnit, {
                        size: lastUnit.info?.physicalSize?.toString() ?? lastUnit.info?.sizeInM2 ?? "",
                        movingBoxes: sizeConfigurations?.movingBoxes[group.shortName] ?? ""
                    });
                }
            }

            let listVolumeString = "";

            if (group.info?.isLastOne) {
                listVolumeString = `${translations.blocks.storageSizesAdvanced.ca} ${volumeString}`;
            } else {
                listVolumeString = `${translations.general.from} ${translations.blocks.storageSizesAdvanced.ca} ${volumeString}`;
            }


            if (group.info?.isLastOne) {

            }

            let orderedUnits = group.unitSizes?.sort((a, b) => {
                return (a.info?.physicalSize ?? 0) - (b.info?.physicalSize ?? 0)
            }) ?? [];

            const sizeMinValue = orderedUnits[0]?.info?.physicalSize ?? 0;
            const sizeMaxValue = orderedUnits[orderedUnits.length - 1]?.info?.physicalSize ?? 0;
            const sizeMinValueString = sizeMinValue.toFixed(1).endsWith(".0") ? parseInt(sizeMinValue.toFixed(1)).toString() : sizeMinValue.toFixed(1);
            const sizeMaxValueString = sizeMaxValue.toFixed(1).endsWith(".0") ? parseInt(sizeMaxValue.toFixed(1)).toString() : sizeMaxValue.toFixed(1);
            const sizeMinMaxString = sizeMinValueString === sizeMaxValueString ? sizeMinValueString : `${sizeMinValueString} - ${sizeMaxValueString}`;

            const storageItem: iStorageSizeAdvanced = {
                title: group.name,
                hasAvailable: group.info?.hasAvailable,
                isLastOne: group.info?.isLastOne,
                hasFewLeft: group.info?.hasFewLeft ?? false,
                name: group.name,
                shortName: group.shortName,
                price: price || 0,
                size: sizeMinMaxString,
                firstMonthDiscount: group.info?.hasDiscount ?? false,
                specialPrice: specialPrice ?? 0,
                description: group.info?.sizeInM2,
                link: cardLink,
                movingBoxesCount: sizeConfigurations?.movingBoxes[group.shortName] ?? "",
                id: group.sizeCodeId ?? group.shortName,
                identifier: `${group.shortName}`,
                isHeated: group.info?.isHeated,
                isCold: group.info?.isCold,
                hasDirectAccess: group.info?.hasDirectAccess,
                hasStairAccess: group.info?.hasStairAccess,
                isGroundFloor: group.info?.isGroundFloor,
                hasSlantedRoof: group.info?.hasSlantedRoof,
                volume: group.info?.isLastOne ? volumeString : `${translations.blocks.storageSizesAdvanced.ca} ${volumeString}`,
                infoTitle: infoTitle,
                infoDescription: infoDescription,
                infoVolumeString: listVolumeString
            };

            let infoAccessList: string[] = [];

            if (storageItem.isGroundFloor) {
                infoAccessList.push(translations.general.groundFloor);
            } else if (storageItem.hasStairAccess) {
                infoAccessList.push(translations.general.stairAccess);
            } else {
                infoAccessList.push(translations.general.elevatorPlane);
            }

            if (!storageItem.hasDirectAccess) {
                infoAccessList.push(translations.general.directAccess);
            }

            if (storageItem.hasSlantedRoof) {
                infoAccessList.push(translations.general.slantedRoof);
            }

            if (infoAccessList.length > 0) {
                storageItem.infoAccessString = infoAccessList.join(", ");
            }

            storageSizes.push(storageItem)
        }
    });

    return storageSizes;
}

function populateGroupSizeRange(group: iSizeGroup) {
    if (group.unitSizes && group.unitSizes.length > 0) {
        let minSize: number | undefined = Infinity;
        let maxSize: number | undefined = -Infinity;

        group.unitSizes.forEach(unitSize => {
            if (unitSize.units && unitSize.units.length > 0) {
                unitSize.units.forEach(unit => {
                    minSize = unit.info?.physicalSize && unit.info?.physicalSize < (minSize ?? Infinity) ? unit.info?.physicalSize : minSize;
                    maxSize = unit.info?.physicalSize && unit.info?.physicalSize > (maxSize ?? -Infinity) ? unit.info?.physicalSize : maxSize;
                });
            } else {
                minSize = unitSize.info?.physicalSize && unitSize.info?.physicalSize < (minSize ?? Infinity) ? unitSize.info?.physicalSize : minSize;
                maxSize = unitSize.info?.physicalSize && unitSize.info?.physicalSize > (maxSize ?? -Infinity) ? unitSize.info?.physicalSize : maxSize;
            }
        });

        if (minSize && minSize !== Infinity) {
            group.minSize = minSize.toFixed(1).endsWith(".0") ? parseInt(minSize.toFixed(1)).toString() : minSize.toFixed(1);
        } else {
            group.minSize = undefined;
        }

        if (maxSize && maxSize !== Infinity) {
            group.maxSize = maxSize.toFixed(1).endsWith(".0") ? parseInt(maxSize.toFixed(1)).toString() : maxSize.toFixed(1);
        } else {
            group.maxSize = undefined;
        }

        if (group.minSize && group.maxSize && group.minSize !== group.maxSize) {
            group.sizeRange = `${group.minSize} - ${group.maxSize}`;
        } else {
            group.sizeRange = group.minSize;
        }
    }

    return
}

function generateAllUnitCardsFromGroup(group: iSizeGroup, sizeConfigurations: iSizeConfigurations, orderStep1Url: string, translations: any): iStorageSizeAdvanced[] {
    let storageSizes: iStorageSizeAdvanced[] = [];

    if (!group.unitSizes || group.unitSizes.length === 0) {
        return storageSizes;
    }

    group.unitSizes.forEach(unitSize => {
        if (!unitSize.units || unitSize.units.length === 0) {
            return;
        }

        const unitCards = generateUnitCardsFromUnits(unitSize.units, group.shortName, sizeConfigurations, orderStep1Url, translations);
        storageSizes.push(...unitCards);
    });

    return storageSizes;
}